import Data from 'data';
import { memoizedCompute } from 'utils';
import { missileFormula, skillFormula } from './calc';
import skillFunctions from './functions';
import { defaultInfo } from './skillinfo';

function calculateDowntime(chance, length, interval) {
  length /= interval;
  const ilength = Math.floor(length);
  const flength = length - ilength;
  const chance2 = (ilength ? Math.pow(1 - chance, ilength) : 1);
  const chance1 = chance2 * (1 - chance);
  return flength * chance1 + (1 - flength) * chance2;
}

export class DPSCalculator {
  entries = {};
  entriesSrc = {};
  total = {
    physical: 0,
    fire: 0,
    cold: 0,
    lightning: 0,
    magic: 0,
    poison: 0,
  };

  // options: interval count chance duration stackable
  damage(name, calc, options) {
    if (!calc) return this;
    if (options.count === 0) return this;
    const entry = this.entries[name] = {
      physical: ((calc.applied_physmindam || 0) + (calc.applied_physmaxdam || 0)) / 2,
      fire: ((calc.applied_firemindam || 0) + (calc.applied_firemaxdam || 0)) / 2,
      cold: ((calc.applied_coldmindam || 0) + (calc.applied_coldmaxdam || 0)) / 2,
      lightning: ((calc.applied_lightmindam || 0) + (calc.applied_lightmaxdam || 0)) / 2,
      magic: ((calc.applied_magicmindam || 0) + (calc.applied_magicmaxdam || 0)) / 2,
      poison: ((calc.applied_poisonmindam || 0) + (calc.applied_poisonmaxdam || 0)) / 2,
      poisonlength: calc.display_poisonlength,
      missile: calc._missile,
      ...options,
    };
    if (calc._srcdam || calc.smite || calc.kick) {
      entry.weapon = calc._weapon;
      entry.specialAttack = calc.smite || calc.kick || entry.specialAttack;
    }
    this.entriesSrc[name] = {...entry};
    return this;
  }

  calculate(stats, target, noprocs) {
    let owDowntime = 1, cbRate = 0;
    const poisons = [];
    let dmgDowntime = 1;

    const weaponAttacks = new Map();

    function processEntry(entry) {
      const interval = entry.interval / (entry.count || 1);
      if (entry.poison) {
        let chance = entry.chance || 1;
        let downtime = calculateDowntime(chance, entry.poisonlength, interval);
        if (entry.weapon && !entry.specialAttack && stats.base.crit) {
          const critDowntime = calculateDowntime(chance * stats.base.crit / 100, entry.poisonlength, interval);
          poisons.push({
            rate: entry.poison * 2,
            downtime: critDowntime,
            entry,
          });
          if (critDowntime) {
            downtime /= critDowntime;
          }
        }
        poisons.push({
          rate: entry.poison,
          downtime,
          entry,
        });
      }

      let factor = 25 * (entry.chance || 1) / interval;
      if (entry.duration) {
        const downtime = calculateDowntime(entry.chance || 1, entry.duration, interval);
        if (!entry.stackable) {
          entry.uptime = 1 - downtime;
          delete entry.duration;
          factor = 25 * entry.uptime;
        } else {
          factor *= entry.duration;
        }
        dmgDowntime *= downtime;
      } else {
        dmgDowntime *= 1 - factor / 25;
      }

      if (entry.weapon) {
        if (!entry.specialAttack) {
          const pct1 = stats.base.passive_critical_strike;
          const pct2 = entry.weapon.item_deadlystrike;
          const pct3 = entry.missile ? entry.weapon.passive_mastery_throw_crit : entry.weapon.passive_mastery_melee_crit;
          if (pct1 || pct2 || pct3) {
            const crit = 1 - (1 - Math.min(100, pct1 || 0) / 100) * (1 - Math.min(100, pct2 || 0) / 100) * (1 - Math.min(100, pct3 || 0) / 100);
            entry.crit = crit;
            entry.physical *= 1 + crit;
          }

          if (stats.base.crit) {
            entry.monstercrit = stats.base.crit / 100;
            factor *= 1 + entry.monstercrit;
          }
        }
        if (entry.weapon.item_openwounds) {
          const chance = Math.min(100, entry.weapon?.item_openwounds) / 100 * (entry.chance || 1);
          owDowntime *= calculateDowntime(chance, 199, interval);  
        }
        if (entry.weapon.item_crushingblow) {
          cbRate += 25 * (entry.missile ? 0.5 : 1) * (Math.min(100, entry.weapon.item_crushingblow) / 100) * (entry.chance || 1) / interval;
        }

        if (!weaponAttacks.has(entry.weapon)) {
          weaponAttacks.set(entry.weapon, {attackRate: 0, strikeRate: 0});
        }
        const watk = weaponAttacks.get(entry.weapon);
        if (!entry.missile) {
          watk.attackRate += 1 / entry.interval;
        }
        watk.strikeRate += (entry.chance || 1) / interval;
      }

      entry.physical *= factor;
      entry.fire *= factor;
      entry.cold *= factor;
      entry.lightning *= factor;
      entry.magic *= factor;
      entry.poison *= factor;
      entry.poisonuptime = 0;
    }

    for (const entry of Object.values(this.entries)) {
      processEntry(entry);
    }

    const procs = {};
    function procSkill(id, level, chance, interval) {
      const skill = Data.skills[id];
      const func = skillFunctions[skill?.srvdofunc || 0];
      if (!skill || !func) return;
      const info = (func.info || defaultInfo).call(func, id, stats, level, target);
      if (info?.damage) {
        info.animation = {fpa: interval, hits: 1};
        const dps = (func.dps || defaultDPS).call(func, id, stats, level, target, info);
        if (dps && !Array.isArray(dps)) {
          for (const [label, entry] of Object.entries(dps.entriesSrc)) {
            const dstid = `skill[${id}].${label}`;
            entry.chance = (entry.chance || 1) * chance;
            if (!procs[dstid]) {
              procs[dstid] = entry;
            } else {
              const dst = procs[dstid];
              const interval = 1 / (1 / dst.interval + 1 / entry.interval);
              const count = interval * ((dst.count || 1) / dst.interval + (entry.count || 1) / entry.interval);
              const rate1 = (dst.chance || 1) * (dst.count || 1) / dst.interval;
              const rate2 = (entry.chance || 1) * (entry.count || 1) / entry.interval;
              const chance = interval / count * (rate1 + rate2);
              dst.interval = interval;
              dst.count = count;
              dst.chance = chance;
              if (dst.duration) dst.duration = Math.max(dst.duration, entry.duration || 0);

              for (const type of ['physical', 'fire', 'cold', 'lightning', 'magic']) {
                dst[type] = (dst[type] * rate1 + entry[type] * rate2) / (rate1 + rate2);
              }
              dst.poisonlength = dst.poison > entry.poison ? dst.poisonlength : entry.poisonlength;
              dst.poison = Math.max(dst.poison, entry.poison);
            }
          }
        }
      }
    }

    if (!noprocs) {
      for (const [weapon, {attackRate, strikeRate}] of weaponAttacks) {
        if (weapon.item_skillonattack && attackRate) {
          for (const [arg, value] of Object.entries(weapon.item_skillonattack)) {
            const [id, level] = arg.split('#');
            procSkill(id, level, value / 100, 1 / attackRate);
          }
        }
        if (weapon.item_skillonhit && strikeRate) {
          for (const [arg, value] of Object.entries(weapon.item_skillonhit)) {
            const [id, level] = arg.split('#');
            procSkill(id, level, value / 100, 1 / strikeRate);
          }
        }
      }
    }

    for (const [id, entry] of Object.entries(procs)) {
      processEntry(entry);
      this.entries[id] = entry;
    }

    if (poisons.length) {
      poisons.sort((a, b) => b.rate - a.rate);
      let downtime = dmgDowntime;
      for (const entry of poisons) {
        const uptime = downtime * (1 - entry.downtime);
        entry.entry.poison += 25 * uptime * entry.rate;
        entry.entry.poisonuptime += uptime;
        downtime *= entry.downtime;
      }
    }
    for (const entry of Object.values(this.entries)) {
      entry.dps = entry.physical + entry.fire + entry.cold + entry.lightning + entry.magic + entry.poison;
      this.total.physical += entry.physical;
      this.total.fire += entry.fire;
      this.total.cold += entry.cold;
      this.total.lightning += entry.lightning;
      this.total.magic += entry.magic;
      this.total.poison += entry.poison;
    }

    if (owDowntime < 1) {
      const stage = Math.min(Math.ceil(stats.level / 15), 4);
      let amount = 9 * (stage + 1) * stats.level + (31 - 135 * stage * (stage + 1) / 2);
      if (target.unit === 'player') {
        amount >>= 2; // divide by 2 for ranged if we ever care about this
      } else if (['champion', 'unique', 'super', 'boss'].includes(target.type)) {
        amount >>= 1;
      }
      amount *= 25 * (1 - owDowntime);
      this.entries.openwounds = {
        physical: amount,
        uptime: 1 - owDowntime,
      };
      this.total.physical += amount;
    }

    if (cbRate) {
      if (target.unit === 'player') {
        cbRate /= 10;
      } else if (target.type === 'super' || target.type === 'boss') {
        cbRate /= 8;
      } else {
        cbRate /= 4;
      }
      if (target.base.damageresist) {
        cbRate *= Math.max(0, Math.min(1, 1 - target.base.damageresist / 100));
      }
      this.crushingblow = target.base.maxhp * cbRate;
    }

    this.dps = this.total.dps = this.total.physical + this.total.fire + this.total.cold + this.total.lightning + this.total.magic + this.total.poison;

    this.ttk = target.base.maxhp / this.dps * 256;
    if (cbRate) {
      this.ttk = Math.log(1 + this.ttk * cbRate) / cbRate;
    }

    return this;
  }

  basic_damage(name, calc, anim, extra={}) {
    if (!calc) return this;
    let chance = 1;
    if (calc._tohit) {
      chance = calc.display_hitchance / 100;
    }
    if (calc.display_blockchance) {
      chance *= 1 - calc.display_blockchance / 100;
    }
    if (chance < 1) {
      extra.chance = (extra.chance == null ? 1 : extra.chance) * chance;
    }
    if (Array.isArray(anim)) anim = anim[0];
    extra.interval = anim.fpa;
    if (anim.chance) extra.chance = (extra.chance ?? 1) * anim.chance;
    if (!extra.count) extra.count = anim.hits;
    return this.damage(name, calc, extra);
  }
}

const sizeParams = [null, [0, 0], [1, 0], [2, 1]];

const collisionArea = [...Array(4)].map((_, a) => [...Array(4)].map((_, b) => {
  if (!a || !b) return 0;
  const r = sizeParams[a][0] + sizeParams[b][0], d = sizeParams[a][1] + sizeParams[b][1];
  return r * (r + 1) * 2 + 1 - 4 * d * d;
}));

const collisionMask = [...Array(3)].map((_, x) => [...Array(3)].map((_, y) => {
  let mask = 0;
  for (let a = 1; a <= 3; ++a) {
    for (let b = 1; b <= 3; ++b) {
      const [r1, d1] = sizeParams[a], [r2, d2] = sizeParams[b];
      const mx = r1 + r2 - d1 - d2;
      if (x + y <= r1 + r2 && x <= mx && y <= mx) {
        mask |= (1 << (a * b));
      }
    }
  }
  return mask;
}));

export const collisionMeteor = memoizedCompute((size1, size2) => {
  if (!size1 || !size2) return 0;
  const coords = [[-5, 0], [-3, -3], [-3, 0], [-3, 3], [-2, 0], [-2, 1], [-1, -1],
    [1, -2], [1, 1], [2, -4], [2, -2], [2, 2], [2, 3], [2, 4], [3, -3], [3, -1], [3, 1], [4, 0]];
  return coords.reduce((count, [x, y]) => count + (((collisionMask[Math.abs(x)]?.[Math.abs(y)] || 0) >> (size1 * size2)) & 1), 0);
});

export const collisionRadius = memoizedCompute((size1, size2, radius) => {
  let hits = 1, area = 1;
  for (let x = 1; x < radius; ++x) {
    for (let y = 0; y < radius; ++y) {
      if (x * x + y * y >= radius * radius) continue;
      area += 4;
      hits += 4 * (((collisionMask[x]?.[y] || 0) >> (size1 * size2)) & 1);
    }
  }
  return [area, hits];
});

export const collisionSquare = memoizedCompute((size1, size2, radius) => {
  let hits = 1, area = 1;
  for (let x = 1; x <= radius; ++x) {
    for (let y = 0; y <= radius; ++y) {
      area += 4;
      hits += 4 * (((collisionMask[x]?.[y] || 0) >> (size1 * size2)) & 1);
    }
  }
  return [area, hits];
});

export function missileDPS(skill, missile, stats, level, target, info, count) {
  if (!info.animation) return;
  const calc = new DPSCalculator();
  const anim = Array.isArray(info.animation) ? info.animation[0] : info.animation;

  const submissile = Data.missileByName[missile.submissile1];
  const hitsubmissile = Data.missileByName[missile.hitsubmissile1];

  // options: interval count chance duration stackable
  switch (missile.psrvdofunc) {
  case 2: // poison javelin
    calc.basic_damage('missile', info.damage.missile, anim);
    calc.basic_damage('trail', info.damage.trail, anim);
    break;
  case 10: // blizzard
    {
      const count = missileFormula(missile, 'rang', stats, level) / skillFormula(skill, 'clc2', stats, level);
      const [area, hits] = collisionRadius(target.size, submissile?.size, skillFormula(skill, 'clc1', stats, level));
      calc.damage('shard', info.damage.shard, {interval: anim.fpa, count: hits * count / area}); // fixed with 'count' param
    }
    break;
  case 12: // diabwall
  case 23: // firestorm
    {
      const missiles = skillFormula(skill, 'clc1', stats, level);
      calc.damage('burn', info.damage.damage, {interval: anim.fpa, count: ([5, 9, 14][target.size - 1] || 0) * missiles / 3, duration: info.damage.damage.frames, stackable: true});
    }
    break;
  case 25: // fissure
    // not entirely accurate because of LastCollide, but that makes things too complicated
    return missileDPS(skill, submissile, stats, level, target, info, count);
  case 26: // vines
    return null;
  case 31: // wake of fire sentry
    calc.basic_damage('missile', info.damage.damage, anim);
    break;
  default:
    switch (missile.psrvhitfunc) {
    case 2: // plague javelin
      calc.basic_damage('missile', info.damage.missile, anim);
      calc.basic_damage('cloud', info.damage.cloud, anim);
      break;
    case 4: // exploding arrow, phoenix strike
      calc.basic_damage('missile', info.damage.missile, anim);
      calc.basic_damage('explosion', info.damage.explosion, anim);
      if (info.damage.burn) {
        calc.damage('burn', info.damage.burn, {interval: anim.fpa, count: collisionMeteor(target.size, hitsubmissile?.size), duration: info.damage.burn.frames, stackable: true});
      }
      break;
    case 9: // immolation arrow
      calc.basic_damage('missile', info.damage.missile, anim);
      calc.basic_damage('explosion', info.damage.explosion, anim);
      calc.damage('burn', info.damage.burn, {interval: anim.fpa, count: collisionArea[target.size]?.[hitsubmissile?.size] || 0, duration: info.damage.burn.frames, stackable: true});
      break;
    case 14: // meteor
      calc.basic_damage('explosion', info.damage.damage, anim);
      calc.damage('burn', info.damage.burn, {interval: anim.fpa, count: collisionMeteor(target.size, hitsubmissile?.size), duration: info.damage.burn.frames, stackable: true});
      break;
    case 20: // lightning fury
      calc.basic_damage('missile', info.damage.missile, anim);
      calc.basic_damage('bolt', info.damage.bolt, anim);
      break;
    case 22: // fist of the heavens
      calc.basic_damage('missile', info.damage.missile, anim);
      calc.basic_damage('bolt', info.damage.bolt, anim);
      break;
    case 29: // frozen orb
      return null;
    case 36: // bomb in air (fire blast)
    case 48: // molten boulder emerge
      return missileDPS(skill, hitsubmissile, stats, level, target, info, count);
    case 47: // molten boulder
      return null;
    case 56: // armageddon
      {
        const [area1, hits1] = collisionSquare(target.size, missile.shitpar1, skill.param3);
        const [area2, hits2] = collisionSquare(target.size, hitsubmissile?.size, skill.param3);
        calc.damage('missile', info.damage.damage, {interval: skill.param4, chance: hits1 / area1});
        calc.damage('burn', info.damage.burn, {interval: skill.param4, chance: hits2 / area2, duration: info.damage.burn.frames, stackable: true});
      }
      break;
    default:
      if (!missile.vel && !missile.vellev && missile.nextdelay) {
        calc.damage('missile', info.damage.damage, {interval: missile.nextdelay + 1});
      } else {
        calc.basic_damage('missile', info.damage.damage, anim);
      }
    }
  }

  if (count != null) {
    for (const entry of Object.values(calc.entries)) {
      entry.count = (entry.count || 1) * count;
      if (entry.count < 1) {
        entry.interval /= entry.count;
        entry.count = 1;
      }
    }
  }

  return calc.calculate(stats, target);
}

export function defaultDPS(id, stats, level, target, info, allResults) {
  if (!info?.damage || !info?.animation) return;
  const skill = Data.skills[id];
  const missile = Data.missileByName[skill.srvmissile];
  if (missile) {
    return missileDPS(skill, missile, stats, level, target, info);
  } else if (this.damage && info.damage) {
    const calc = new DPSCalculator();
    const anim = Array.isArray(info.animation) ? info.animation[0] : info.animation;
    if (info.damage.damage) {
      calc.basic_damage('damage', info.damage.damage, anim);
    }
    if (info.damage.offhand) {
      calc.basic_damage('offhand', info.damage.offhand, anim);

      if (calc.entries.damage && calc.entries.offhand) {
        if (calc.entries.damage.count > 1) {
          calc.entries.damage.count /= 2;
          calc.entries.offhand.count /= 2;
        } else {
          calc.entries.damage.interval *= 2;
          calc.entries.offhand.interval *= 2;
        }
      }
    }
    return calc.calculate(stats, target);
  }
}
